import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Check, X, MagnifyingGlass, ArrowsDownUp } from '@phosphor-icons/react';

export default function ExpensesHub() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [categories, setCategories] = useState([]);
  const [centralFund, setCentralFund] = useState(0);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      const { data } = await api.get(`/expenses?${params}`);
      setExpenses(data);
    } catch {}
    setLoading(false);
  }, [filterStatus, filterCategory]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => {
    api.get('/admin/categories?type=expense').then(r => setCategories(r.data)).catch(() => {});
    api.get('/dashboard/stats').then(r => setCentralFund(r.data.central_fund_balance || 0)).catch(() => {});
  }, []);

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') { await api.put(`/expenses/${id}/approve`); toast.success('Expense approved'); }
      else { await api.put(`/expenses/${id}/deny`, { reason: 'Denied' }); toast.success('Expense denied'); }
      fetchExpenses();
      api.get('/dashboard/stats').then(r => setCentralFund(r.data.central_fund_balance || 0)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Expense Form
  const ExpenseForm = () => {
    const [form, setForm] = useState({ category: '', amount: '', description: '', subcategory: '', vendor: '', date: new Date().toISOString().split('T')[0] });
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
      if (!form.category || !form.amount || !form.description) { toast.error('Category, amount and description required'); return; }
      setSaving(true);
      try {
        await api.post('/expenses', { ...form, amount: parseFloat(form.amount) });
        toast.success('Expense created');
        setShowForm(false); fetchExpenses();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };
    return (
      <Dialog open onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="expense-form-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">New Expense</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="data-label">Category *</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="rounded-sm" data-testid="expense-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name} ({c.subtype})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Amount (USD) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-sm" data-testid="expense-amount-input" /></div>
              <div><Label className="data-label">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-sm" /></div>
            </div>
            <div><Label className="data-label">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm" rows={2} data-testid="expense-desc-input" /></div>
            <div><Label className="data-label">Vendor</Label>
              <Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} className="rounded-sm" /></div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm text-slate-600">
              Central Fund Balance: <span className="currency font-semibold">${centralFund.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="expense-save-btn">
              {saving ? 'Saving...' : 'Submit for Approval'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const statusColor = (s) => ({ pending: 'status-pending', approved: 'status-approved', denied: 'status-denied' }[s] || '');

  return (
    <div className="space-y-4" data-testid="expenses-hub">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Expenses</h2>
          <p className="text-sm text-slate-500">Central Fund: <span className="currency font-medium">${centralFund.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-sm" data-testid="create-expense-btn">
          <Plus size={16} className="mr-1" /> New Expense
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all_status">All Status</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44 rounded-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all_cats">All Categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-sm w-40" data-testid="expense-date-from" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-sm w-40" data-testid="expense-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear dates</Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {[
              { key: 'date', label: 'Date' },
              { key: 'category', label: 'Category' },
              { key: 'amount', label: 'Amount' },
              { key: 'description', label: 'Description' },
              { key: 'vendor', label: 'Vendor' },
              { key: 'status', label: 'Status' },
              { key: 'created_by_name', label: 'Created By' },
              { key: '_actions', label: 'Actions' },
            ].map(col => (
              <th key={col.key}
                className={`text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4 ${col.key !== '_actions' ? 'cursor-pointer select-none hover:text-slate-800' : ''}`}
                onClick={() => { if (col.key !== '_actions') { setSortField(col.key); setSortDir(sortField === col.key && sortDir === 'asc' ? 'desc' : 'asc'); } }}>
                <span className="flex items-center gap-1">{col.label}
                  {sortField === col.key && col.key !== '_actions' && <ArrowsDownUp size={10} className="text-[#0F6CBD]" />}
                </span>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-400">Loading...</td></tr> :
              (() => {
                let filtered = [...expenses];
                if (dateFrom) filtered = filtered.filter(e => (e.date || '') >= dateFrom);
                if (dateTo) filtered = filtered.filter(e => (e.date || '') <= dateTo);
                filtered.sort((a, b) => {
                  const av = a[sortField], bv = b[sortField];
                  if (sortField === 'amount') return sortDir === 'asc' ? (av||0)-(bv||0) : (bv||0)-(av||0);
                  return sortDir === 'asc' ? String(av||'').localeCompare(String(bv||'')) : String(bv||'').localeCompare(String(av||''));
                });
                if (filtered.length === 0) return <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-400">No expenses</td></tr>;
                return filtered.map(e => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`expense-row-${e.id}`}>
                  <td className="py-3 px-4 text-sm text-slate-600">{e.date}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{e.category}</td>
                  <td className="py-3 px-4 currency text-sm font-medium">${(e.amount || 0).toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">{e.description}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{e.vendor}</td>
                  <td className="py-3 px-4"><Badge className={`capitalize text-xs ${statusColor(e.status)}`}>{e.status}</Badge></td>
                  <td className="py-3 px-4 text-xs text-slate-500">{e.created_by_name}</td>
                  <td className="py-3 px-4">
                    {e.status === 'pending' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600 text-xs" onClick={() => handleAction(e.id, 'approve')} data-testid={`approve-expense-${e.id}`}>
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 text-xs" onClick={() => handleAction(e.id, 'deny')} data-testid={`deny-expense-${e.id}`}>
                          <X size={14} className="mr-1" /> Deny
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ));
              })()}
          </tbody>
        </table>
      </div>
      {showForm && <ExpenseForm />}
    </div>
  );
}
